/**
 * Browser persistence is only for low-sensitivity UI preferences. User
 * identity, conversations, documents, notifications, generated artifacts and
 * component snapshots must be reloaded from their authorized server sources.
 */
export const SENSITIVE_APP_STORE_KEYS = new Set([
  "userSession",
  "chatSession",
  "fileList",
  "notifications",
  // Where the feed read stands this session; a reload must wait for it again.
  "notificationFeedStatus",
  // Optimistic read/done of feed rows: memory only, the feed state is durable.
  // `dismissedNotificationKeys` is the retired localStorage copy, dropped on load.
  "readNotificationKeys",
  "pendingDoneNotificationKeys",
  "pendingReopenNotificationKeys",
  "dismissedNotificationKeys",
  "pendingChatMessage",
  "sendingChatMessage",
  "activeCall",
  "joinedNativeCall",
  "documentViewer",
  "mediaGallery",
  "artifactsById",
  "componentsByWindow",
  "deletedComponentsByWindow",
  "editHistoryByWindow",
  "aiActivityByComponent",
  "isSigningOut",
  "workspaceSaveIssue",
  "wakeWordPrompt",
  "liveAudioData",
]);

export function sanitizePersistedAppState(
  state: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!state || typeof state !== "object") return {};

  return Object.fromEntries(
    Object.entries(state).filter(([key]) => !SENSITIVE_APP_STORE_KEYS.has(key)),
  );
}

export const LEGACY_BROWSER_SECRET_KEYS = [
  "accessToken",
  "encryptedSmartManagerStore",
  "workos_session",
] as const;

export function purgeLegacyBrowserSecrets(storage: Pick<Storage, "removeItem">): void {
  for (const key of LEGACY_BROWSER_SECRET_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // Storage may be unavailable in privacy-restricted browser contexts.
    }
  }
}
